import { useState } from 'react';
import styled from 'styled-components';

import { VFlex } from '@/components/base';
import { ProgressIndicator } from '@/components/Chart/ProgressBar/ProgressBar';
import ScrollView from '@/components/Common/ScrollView';
import AMP_KEYS from '@/configs/amp-track-keys';
import DnDFileUploader from '@/features/CsvImport/DnDFileUploader';
import { UPLOAD_STATE } from '@/features/CsvImport/UploadPreview';
import service from '@/services/index';
import useToastStore from '@/stores/pure/ToastStore';
import { track } from '@/utils/track/track';

type Props = {
  closeUploadModal: () => void;
};

const FeedbackJsonUploadBody = (props: Props) => {
  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));

  const [uploading, setUploading] = useState<UPLOAD_STATE>(UPLOAD_STATE.READY);
  const [progress, setProgress] = useState({
    current: 1,
    total: 100,
  });
  const [dragActive, setDragActive] = useState(false);

  const uploadData = async (data: JSONFeedbackUploadParams[]) => {
    setUploading(UPLOAD_STATE.UPLOADING);
    setProgress((state) => ({
      ...state,
      total: data.length,
    }));
    try {
      const chunkSize = 300;

      for (let i = 0; i < data.length; i += chunkSize) {
        const chunk = data.slice(i, i + chunkSize);
        try {
          await service.feedback.uploadFeedback(chunk);
          setProgress((state) => ({
            ...state,
            current: state.current + chunkSize,
          }));
        } catch (e) {
          alert(
            `Upload failed at chunk ${Math.floor(
              i / chunkSize,
            )}. Please try again starting from row ${i}.`,
          );
          throw e;
        }
      }
      setUploading(UPLOAD_STATE.COMPLETE);

      // Track feedback upload complete event
      track(AMP_KEYS.FEEDBACK_UPLOAD_COMPLETE, {
        feedback_count: data.length,
        upload_type: 'json',
      });

      addDefaultToast('The file has been successfully uploaded', 'success');
    } catch (e) {
      setUploading(UPLOAD_STATE.READY);
      addDefaultToast('Upload Fail', 'fail');
    }
  };

  if (uploading === UPLOAD_STATE.UPLOADING)
    return (
      <ProgressIndicator current={progress.current} total={progress.total} />
    );

  return (
    <ScrollView style={{ height: '100%' }}>
      <Wrapper g={20}>
        <DnDFileUploader
          uploadData={uploadData}
          dragActive={dragActive}
          setDragActive={setDragActive}
        />
      </Wrapper>
    </ScrollView>
  );
};

export default FeedbackJsonUploadBody;

const Wrapper = styled(VFlex)`
  padding: 16px 32px;
`;
